import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} menu - The custom context menu.
 */

const MEDIA_SELECTOR = 'img, picture, video';
const EDGE_MARGIN = 8;
const VISIBLE_DURATION = 2500;

/**
 * Replaces the browser context menu with a notice when it is opened on media,
 * and blocks dragging media out of the page.
 *
 * @extends {Component<Refs>}
 */
class ImageProtection extends Component {
  requiredRefs = ['menu'];

  #controller = new AbortController();
  #open = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hideTimeout;

  connectedCallback() {
    super.connectedCallback();

    // Merchants need the native menu (inspect, open in new tab) while working in the theme editor.
    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    const { signal } = this.#controller;

    document.addEventListener('contextmenu', this.#handleContextMenu, { signal });
    document.addEventListener('dragstart', this.#handleDragStart, { signal });
    document.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    document.addEventListener('keydown', this.#handleKeyDown, { signal });
    window.addEventListener('scroll', this.#hide, { signal, passive: true });
    window.addEventListener('resize', this.#hide, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    clearTimeout(this.#hideTimeout);
  }

  /**
   * @param {MouseEvent} event - The contextmenu event.
   */
  #handleContextMenu = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest(MEDIA_SELECTOR)) {
      this.#hide();
      return;
    }

    event.preventDefault();
    this.#show(event.clientX, event.clientY);
  };

  /**
   * @param {DragEvent} event - The dragstart event.
   */
  #handleDragStart = (event) => {
    if (event.target instanceof Element && event.target.closest(MEDIA_SELECTOR)) event.preventDefault();
  };

  /**
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handlePointerDown = (event) => {
    // The secondary button is followed by `contextmenu`, which repositions the menu instead.
    if (event.button === 2) return;

    this.#hide();
  };

  /**
   * @param {KeyboardEvent} event - The keydown event.
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') this.#hide();
  };

  /**
   * @param {number} x - Viewport x coordinate of the pointer.
   * @param {number} y - Viewport y coordinate of the pointer.
   */
  #show(x, y) {
    const { menu } = this.refs;

    if (!this.#open) {
      menu.showPopover();
      this.#open = true;
    }

    // `offsetWidth` over `getBoundingClientRect`, which reports the mid-transition scale.
    const left = Math.min(Math.max(x, EDGE_MARGIN), window.innerWidth - menu.offsetWidth - EDGE_MARGIN);
    const top = Math.min(Math.max(y, EDGE_MARGIN), window.innerHeight - menu.offsetHeight - EDGE_MARGIN);

    menu.style.setProperty('--image-protection-x', `${Math.max(left, EDGE_MARGIN)}px`);
    menu.style.setProperty('--image-protection-y', `${Math.max(top, EDGE_MARGIN)}px`);

    clearTimeout(this.#hideTimeout);
    this.#hideTimeout = setTimeout(this.#hide, VISIBLE_DURATION);
  }

  #hide = () => {
    clearTimeout(this.#hideTimeout);

    if (!this.#open) return;

    this.refs.menu.hidePopover();
    this.#open = false;
  };
}

if (!customElements.get('image-protection')) customElements.define('image-protection', ImageProtection);
